import React, { memo, useState, useEffect, useContext } from "react";
import { Grid } from "@mui/material";
import styled from "styled-components";
import StatisticBox from "../../common/StatisticBox";
import BestIBSMapElements from "./BestIBSMapElements";
import { adminLevelToAdm } from "../../../../constants/WebMapApi";
import { getFilters } from "../../../../services/api/filterService";
import ScrollableMultiSelect from "../../common/ScrollableMultiSelect";
import { HorizontalSeparator } from "../../../styled/SidePanel.style";
import { convertArrayToStringWithDelimeter } from "../../../../utils/helper";
import { WebMapContext } from "../../../../pages/sections/web-map/WebMapGoogleMap";
import { DATA_ORDERS, ICON_MAP, LABEL_RENAME, Names_For_Filter_OPs, STATIC_ATTRS } from "../../../../constants/WebMap";
import { CommonRadioSelectGroup, CommonRangeSelectSlider, CommonTypeTabsGroup } from "../../common";
import { addDynamicIcons, renameLabels, sortByReference } from "../../../../helpers/fuctionHelpers";
import { fetchBuildingCount, fetchBuildingSearchOptions, fetchMinMaxForAttr } from "../../../../services/api/mapIBS.api";

const StyledContainer = styled.div`
  padding-top: 5px;
  margin: ${({ margin }) => {
    return margin ? `${margin};` : "10px 5px;"
  }};
  width: 100%;
`;

const StyledGridContainer = styled(Grid)`
  && {
    justify-content: space-between;
    margin: 15px 5px 0px;
    padding: 10px;
    width: 100%;
    font: normal normal 550 13px/18px Open Sans;
    background: #ffffff 0% 0% no-repeat padding-box;
    border: 1px solid #007842;
    border-radius: 5px;
    opacity: 1;
    margin-bottom: 10px;
  }
`;

const BestIBSPanel = ({
  isIBSChecked, isIBSVisible
}) => {
  const [connectionTypes, setConnectionTypes] = useState([]);
  const [ct, setCt] = useState("");
  const [attr, setAttr] = useState("");
  const [attrTypes, setAttrTypes] = useState([]);
  const [aggrTypes, setAggrTypes] = useState([]);
  const [aggrType, setAggrType] = useState("");
  const [buildCounts, setBuildCounts] = useState({filtered: 0,nation: 0});
  const [selectedBuildNames, setSelectedBuildNames] = useState([]);
  const [initBuildNames, setInitBuildNames] = useState([]);
  const [selectedBuildTypes, setSelectedBuildTypes] = useState([]);
  const [initBuildTypes, setInitBuildTypes] = useState([]);
  const [range, setRange] = useState([0, 1]);
  const [filtersTrigger, setFiltersTrigger] = useState(0);
  const [selectedTableBuild, setSelectedTableBuild] = useState();
  const [gradientAttr, setGradientAttr] = useState([]); 
  const [attrLimits, setAttrLimits] = useState();

  const { appliedFilters, geohashNE, geohashSW, getBbox, ibsConfig, operatorsPermittedBestIBS } = useContext(WebMapContext);
  const { countryCode, year, quarter, level, levelVal } = appliedFilters;

  useEffect(() => {
    setBuildCounts({ nation: 0, filtered: 0 });
    setConnectionTypes([]);
    setInitBuildTypes([]);
    setSelectedBuildTypes([]);
    setSelectedBuildNames([]);
    setInitBuildNames([]);
    setSelectedTableBuild();
    setAggrTypes([]);
    setAttrTypes([]);
  }, [countryCode, year, quarter]);
  const [operatorDetails, setOperatorData] = useState([]);

  useEffect(() => {
    if (year && quarter && isIBSChecked) getBuildingCount("nation");
  }, [isIBSChecked, year, quarter]);

  useEffect(() => {
    const init = async () => {
      if(isIBSChecked || isIBSVisible){
        if(operatorsPermittedBestIBS?.length){
          setOperatorData(operatorsPermittedBestIBS);
        }
      }
    };
    init();
  }, [isIBSChecked, isIBSVisible, operatorsPermittedBestIBS])

  useEffect(() => {
    if (levelVal && buildCounts.nation > 0) {
      getBuildingCount("filtered");
    } else {
      setBuildCounts({ ...buildCounts, filtered: 0 });
    }
  }, [level, levelVal, selectedBuildNames, selectedBuildTypes, range, buildCounts.nation]);

  useEffect(() => {
    if (year && quarter && (isIBSChecked || isIBSVisible) && connectionTypes.length === 0) {
      fetchCTFilters(); // initial fetch
    }
  }, [isIBSChecked,connectionTypes,isIBSVisible]);

  useEffect(() => {
    if (isIBSChecked && ct) { // fetch build types when bbox change
      getBuildTypeOptions(1, null, { ct}).then(types => {
        setInitBuildTypes(types);
        setSelectedBuildTypes(selectedBuildTypes.filter(x => types.includes(x)));
      });
    }
  }, [getBbox, isIBSChecked, level, levelVal]);

  useEffect(() => {
    if (isIBSChecked && ct) { // fetch build names when bbox change
      getBuildNameOptions(1, null, { ct }).then(names => {
        setInitBuildNames(names);
        setSelectedBuildNames(selectedBuildNames.filter(x => names.includes(x)));
      });
    }
  }, [getBbox, isIBSChecked, level, levelVal]);

  useEffect(() => {
    if (selectedTableBuild) setSelectedTableBuild(); // reset
  }, [countryCode, year, quarter, level, levelVal, ct, range, aggrType]);

  const getBuildingCount = async (type) => {
    const { data } = await fetchBuildingCount(
      {
        country: countryCode,
        quarter: `${quarter} ${year}`,
        ...(type === "filtered" && {
          level: level !== 'geoHashLevel' ? adminLevelToAdm[level] : null,
          levelVal: level !== 'geoHashLevel' ? levelVal : null,
          scoreFrom: range[0],
          scoreTo: range[1],
          buildNames: selectedBuildNames.length ? convertArrayToStringWithDelimeter(selectedBuildNames, "_") : null,
          buildTypes: selectedBuildTypes.length ? convertArrayToStringWithDelimeter(selectedBuildTypes, "_") : null,
        })
      }
    );
    if (data) setBuildCounts({ ...buildCounts, [type]: data?.count })
  }

  const getBuildSearchOptions = async (key, page, searchVal, params = {}) => {
    const { neBound, swBound } = getBbox;
    try {
      const { data } = await fetchBuildingSearchOptions({
        country: countryCode,
        quarter: `${quarter} ${year}`,
        ct: params.ct || ct,
        level: level !== 'geoHashLevel' ? adminLevelToAdm[level] : null,
        levelVal: level !== 'geoHashLevel' ? levelVal : null,
        northEast: convertArrayToStringWithDelimeter(geohashNE.length ? geohashNE : neBound, "_"),
        southWest: convertArrayToStringWithDelimeter(geohashSW.length ? geohashSW : swBound, "_"),
        scoreFrom: params.scFrom || range[0],
        scoreTo: params.scTo || range[1],
        ...(key === "name" && { type: convertArrayToStringWithDelimeter(params.type || selectedBuildTypes, "_")} ), // building types (only when searching build. names)
        searchKey: key,
        ...(searchVal && { searchVal })
      }, page, 20);
      if (data && data.length) {
        return data
      } else return [];
    } catch (e) {
      return [];
    }
  }

  const getBuildTypeOptions = (page, searchVal, params) => getBuildSearchOptions("type", page, searchVal, params);
  const getBuildNameOptions = (page, searchVal, params) => getBuildSearchOptions("name", page, searchVal, params);

  const getMinMaxForAttr = async ({ attri = attr }, cb) => {
    try {
      const { data } = await fetchMinMaxForAttr({
        country: countryCode,
        quarter: `${quarter} ${year}`,
        ct,
        attr: attri,
        aggr: "SUM"
      });
      if (data) {
        setAttrLimits(data[0]);
        if (cb) cb(); // callback
      }
    } catch (e) {
      console.log('Fetch gradient min max failed: ', e)
    }
  }

  // common func. to fetch values for radio filters.
  const fetchRadioFilters = async (table, column, query, cb) => {
    try {
      if (countryCode && year && quarter) {
        const { data } = await getFilters(
          {
            countryCode,
            year,
            quarter,
            column,
            table,
          },
          query
        );
        if (data) {
          const options = data.map((x) => ({
            label: x._id,
            value: x._id,
          }));
          if (cb) cb(options);
        }
      }
    } catch (err) {
      console.log("Error while fetching ibs matched filters: ", err);
    }
  };

  // fetch Attribute type values
  const fetchAttrTypes = ({conType, sc = range }) => {
    fetchRadioFilters("ibs_operator_measures", "attr", {
      params: {
        filters: `ct:${conType || ct}`,
        ...((sc[0] > 0 || sc[1] < 1) && { logicFilters: `f_score:${sc[0] > 0 ? sc[0] + "$gte" : ""}${sc[0] > 0 && sc[1] < 1 ? "|" : ""}${sc[1] < 1 ? sc[1] + "$lte" : ""}` })
      },
    }, (data) => {
      const arr = sortByReference(data, DATA_ORDERS.ATTR_TYPE, "label");
      const renamed = renameLabels(arr, LABEL_RENAME);
      const allAttr = [...renamed, { label: "Score", value: "Score" }];
      const withIcons = addDynamicIcons(allAttr, ICON_MAP);
      setAttrTypes(withIcons);
      const isCurrentVal = withIcons.some(x => x.value === attr); // check current val is already available
      const currentGradientAttr = ibsConfig[conType]?.[0]?.content.filter(x => x.values && "colorGradient" in x.values[0]).map(y => y.type) || [];
      if(currentGradientAttr){
      setGradientAttr(currentGradientAttr);
      }
      if (!isCurrentVal) {
        const selectedAttr = withIcons[0].value;
        if (currentGradientAttr.includes(selectedAttr)) {
          getMinMaxForAttr({ attri: selectedAttr }, () => {
            setAttr(selectedAttr);
            setTimeout(() => setFiltersTrigger(filtersTrigger + 1), 1000);
          })
        } else {
          setAttr(selectedAttr);
          setTimeout(() => setFiltersTrigger(filtersTrigger + 1), 1000);
        }
      } else setTimeout(() => setFiltersTrigger(filtersTrigger + 1), 1000);
    });
  }

  // fetch Aggregation type values
  const fetchAggrTypes = ({ conType, sc = range }) => {
    fetchRadioFilters("ibs_operator_measures", "aggr", {
      params: {
        filters: `ct:${conType || ct}`,
        ...((sc[0] > 0 || sc[1] < 1) && { logicFilters: `f_score:${sc[0] > 0 ? sc[0] + "$gte" : ""}${sc[0] > 0 && sc[1] < 1 ? "|" : ""}${sc[1] < 1 ? sc[1] + "$lte" : ""}` })
      },
    }, (data) => {
      const sortArr = sortByReference(data, DATA_ORDERS.AGGR_TYPE, "label");
      const aggrArr = sortArr.filter(x => x.value !== "SUM");
      const isCurrentVal = aggrArr.some(x => x.value === aggrType); // check current val is already available
      if (!isCurrentVal && aggrType !== "SUM") setAggrType(aggrArr[0]?.value);
      setAggrTypes(aggrArr);
    });
  }

  const onChangeCTorScoreCallback = ({ conType = ct, sc = range }) => {
    setSelectedBuildTypes([]);
    setSelectedBuildNames([]);
    getBuildTypeOptions(1, null, { ct: conType, scFrom: sc[0], scTo: sc[1] }).then(types => {
      setInitBuildTypes(types);
    });
    getBuildNameOptions(1, null, { ct: conType, type: [], scFrom: sc[0], scTo: sc[1] }).then(names => {
      setInitBuildNames(names);
    });
    fetchAggrTypes({ conType, sc });
    fetchAttrTypes({ conType, sc });
  }

  const fetchCTFilters = () => {
    fetchRadioFilters("ibs_operator_measures", "ct", {
      params: {
        filters: ``,
      },
    }, (data) => {
      const ctArr = sortByReference(data, DATA_ORDERS.CT, "label");
      const currentCtExists = ctArr.some(ctItem => ctItem.value === ct);
      if (!currentCtExists) setCt(ctArr[0]?.value);
  
      setConnectionTypes(ctArr);
      setRange([0, 1]); // reset
      onChangeCTorScoreCallback({
        conType: currentCtExists ? ct : ctArr[0]?.value,
        sc: [0, 1]
      });
    });
  }
  
  const handleBuildingTypeChange = (selectedOptions) => {
    setSelectedBuildTypes(selectedOptions);
    setSelectedBuildNames([]);
    if (selectedTableBuild && !selectedOptions.includes(selectedTableBuild.t)) setSelectedTableBuild(); // reset
    getBuildNameOptions(1, null, { type: selectedOptions }).then(names => {
      setInitBuildNames(names);
      setTimeout(() => setFiltersTrigger(filtersTrigger + 1), 1000);
    });
  };

  const handleBuildingNameChange = (selectedOptions) => {
    setSelectedBuildNames(selectedOptions);
    if (selectedTableBuild && !selectedOptions.includes(selectedTableBuild.n)) setSelectedTableBuild(); // reset
    setTimeout(() => setFiltersTrigger(filtersTrigger + 1), 1000);
  };

  return (
    <>
      <Grid
        container
        item
        sx={{
          justifyContent: "space-between",
          textAlign: "left",
          font: "normal normal normal 12px/16px Open Sans",
          letterSpacing: "0px",
          color: "black",
          opacity: "1",
          padding: "10px",
          display: isIBSChecked ? "flex" : "none",
        }}
      >
        
          <StatisticBox
            topic="# of Buildings"
            tooltip={`${buildCounts.filtered > 0 ? "Geographical Area / " : ""}Nationwide`}
            numerator={buildCounts.filtered}
            denominator={buildCounts.nation}
          />
        <StyledContainer>
          <CommonTypeTabsGroup
            data={connectionTypes}
            value={ct}
            handleChange={(val) => {
              setCt(val);
              setRange([0, 1]); // reset
              onChangeCTorScoreCallback({ conType: val, sc: [0, 1] });
            }}
            icon={true}
          />
        </StyledContainer>
        <StyledGridContainer container item>
          <CommonRangeSelectSlider
            title="Score"
            value={range}
            onChange={(min, max) => {
              setRange([min, max]);
              onChangeCTorScoreCallback({ sc: [min, max] });
            }}
            min={0}
            max={1}
            steps={0.1}
          />
          {ct &&
            <StyledContainer margin={"10px 0px"}>
              <ScrollableMultiSelect
                title="Building Type"
                disabled={false}
                control={selectedBuildTypes}
                setControl={handleBuildingTypeChange}
                loadData={getBuildTypeOptions}
                initialData={initBuildTypes}
              />
              <div style={{ height: "10px" }} />
              <ScrollableMultiSelect
                title="Building Name"
                disabled={false}
                control={selectedBuildNames}
                setControl={handleBuildingNameChange}
                loadData={getBuildNameOptions}
                initialData={initBuildNames}
              />
            </StyledContainer>}
        </StyledGridContainer>
        <StyledGridContainer container item>
          {aggrTypes.length > 0 && aggrType &&
            <>
              <CommonTypeTabsGroup
                title="Aggregation Type"
                data={aggrTypes}
                value={aggrType}
                handleChange={(val) => {
                  setAggrType(val);
                  if (!STATIC_ATTRS.includes(attr)) setAttr(attrTypes[0].value);
                  setTimeout(() => setFiltersTrigger(filtersTrigger + 1), 1000);
                }}
                icon={false}
              />
            </>
          }
          <div style={{ height: "10px", width: "100%" }} />
          {attrTypes.length > 0 && attr &&
            <>
              <CommonRadioSelectGroup
                items={attrTypes.filter(x => STATIC_ATTRS.includes(x.value))}
                value={attr}
                onChange={(val) => {
                  if (aggrType === "SUM") setAggrType(aggrTypes[0].value);
                  setAttr(val);
                  setTimeout(() => setFiltersTrigger(filtersTrigger + 1), 1000);
                }}
              />
              {isIBSChecked ? <HorizontalSeparator /> : <></>}
              <CommonRadioSelectGroup
                items={attrTypes.filter(x => !STATIC_ATTRS.includes(x.value))}
                value={attr}
                onChange={(val) => {
                  setAggrType("SUM");
                  if (val !== "Score" && gradientAttr.includes(val)) {
                    getMinMaxForAttr({ attri: val }, () => {
                      setAttr(val);
                      setTimeout(() => setFiltersTrigger(filtersTrigger + 1), 1000);
                    })
                  } else { // score
                    setAttr(val);
                    setTimeout(() => setFiltersTrigger(filtersTrigger + 1), 1000);
                  }
                }}
              />
            </>
          }
        </StyledGridContainer>
      </Grid>

      {isIBSVisible && ibsConfig?.[ct]?.length > 0 && (
        <>
          <BestIBSMapElements
            eyeIcon={isIBSVisible}
            connection_type={ct}
            scoreRangeFrom={range[0]}
            scoreRangeTo={range[1]}
            apiTrigger={filtersTrigger}
            attr={attr}
            aggr={aggrType}
            names={selectedBuildNames}
            types={selectedBuildTypes}
            gradientAttr={gradientAttr}
            attrLimits={attrLimits}
            attrTypes={attrTypes}
            isIBSChecked={isIBSChecked}
            operatorDetails={operatorDetails}
          />
        </>
      )}
    </>
  );
};

export default memo(BestIBSPanel);

